/**
 * Utilities for building Firestore query constraints and client-side filter logic
 * from the dashboard filter state. Also provides helpers for transforming filter
 * objects into Algolia facet-filter arrays and Postgres-compatible structures.
 * @module queryConstraint
 */

import { QueryConstraint, where } from "firebase/firestore";
import moment from "moment";
import { FilterValues } from "../data-model/filterValues";
import { MedicalPaOrder } from "../data-model/medicalPaOrdersModel";
import { MedicalPaOrderFilterKeys } from "../enums/medicalPaKeys";

/**
 * Builds an array of Firestore `QueryConstraint`s from active filter values.
 *
 * @param filterValues - Current filter state from the Redux store.
 * @param searchedTerm - Optional free-text search term for array-contains matching.
 * @returns Array of Firestore where-clauses to apply to a collection query.
 */
export const createQueryConstraintforFilters = (
  filterValues: FilterValues[],
  searchedTerm?: string,
) => {
  const queryConstraints: QueryConstraint[] = [];
  if (searchedTerm && searchedTerm !== "") {
    queryConstraints.push(
      where("search_index", "array-contains", searchedTerm.toLowerCase()),
    );
  }
  const filtersAfterProcessing = processFilterData(filterValues);
  const queryConstarintKey = Object.keys(filtersAfterProcessing);
  for (const key of queryConstarintKey) {
    if (key === MedicalPaOrderFilterKeys.DrugNames) {
      queryConstraints.push(
        createQueryConstraintforDrugNames(filtersAfterProcessing[key]),
      );
    }
    if (key === MedicalPaOrderFilterKeys.PayerNames) {
      queryConstraints.push(
        createQueryConstraintforPayerNames(filtersAfterProcessing[key]),
      );
    }
  }

  return queryConstraints;
};

/**
 * Creates a Firestore "in" constraint for filtering by regimen/drug names.
 *
 * @param drugNames - List of regimen names to match.
 * @returns A Firestore QueryConstraint.
 */
export const createQueryConstraintforDrugNames = (drugNames: string[]) => {
  return where("payload.regimen_name", "in", drugNames);
};

/**
 * Creates a Firestore "in" constraint for filtering by primary payer name.
 *
 * @param payerNames - List of payer names to match.
 * @returns A Firestore QueryConstraint.
 */
export const createQueryConstraintforPayerNames = (payerNames: string[]) => {
  return where("coverage.primary.payer_name", "in", payerNames);
};

/**
 * Extracts and flattens filter values from the Redux filter state into a
 * plain object keyed by filter attribute with arrays of selected values.
 *
 * @param validFilters - Raw filter values array from the store.
 * @returns Flattened filter map (key → selected values).
 */
export const processFilterData = (validFilters: FilterValues[]) => {
  if (!validFilters?.[0]?.["filterCount"]) return {};

  const allFilters = validFilters[0];
  const filters = Object.keys(allFilters).filter((key) => key === "values");
  const filterValues = filters.map((filter) => allFilters[filter]);

  return Object.keys(filterValues?.[0] || {})
    .map((key) => ({
      [key]: filterValues?.[0]?.[key]?.values,
    }))
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});
};

/**
 * Applies all active filters client-side to an array of medical PA orders.
 * Supports drug name, payer, status, auth status, BO status, assignee,
 * date-of-service, created-at, EV write-back status, and document upload status filters.
 *
 * @param filterValues - Current filter state from the Redux store.
 * @param ordersData - Full array of orders to filter.
 * @returns Subset of orders matching all active filters.
 */
export const createFilteredDataForUiManipulation = (
  filterValues: FilterValues[],
  ordersData: MedicalPaOrder[],
) => {
  let filteredData = ordersData;
  const filtersAfterProcessing = processFilterData(filterValues);
  const filterKeys = Object.keys(filtersAfterProcessing);
  for (const key of filterKeys) {
    if (key === MedicalPaOrderFilterKeys.DrugNames) {
      filteredData = filteredData.filter((order) => {
        const drugName = order.payload?.regimen_name;
        if (drugName) {
          return filtersAfterProcessing[key].includes(drugName);
        }
        return false;
      });
    }
    if (key === MedicalPaOrderFilterKeys.PayerNames) {
      filteredData = filteredData.filter((order) => {
        const payerName = order.coverage?.primary?.payer_name;
        if (payerName) {
          return filtersAfterProcessing[key].includes(payerName);
        }
        return false;
      });
    }
    if (key === MedicalPaOrderFilterKeys.Status) {
      filteredData = filteredData.filter((order) => {
        const status = order?.status?.financial_review;
        if (status) {
          return filtersAfterProcessing[key].includes(status);
        }
        return false;
      });
    }
    if (key === MedicalPaOrderFilterKeys.AuthVerificationStatus) {
      filteredData = filteredData.filter((order) => {
        const authStatus = order?.auth_on_file?.auth_status;
        if (authStatus) {
          return filtersAfterProcessing[key].includes(authStatus);
        }
        return false;
      });
    }

    if (key === MedicalPaOrderFilterKeys.BoStatus) {
      filteredData = filteredData.filter((order) => {
        const boStatus = order?.status?.bo_status;
        if (boStatus) {
          return filtersAfterProcessing[key].includes(boStatus);
        }
        return false;
      });
    }

    if (key === MedicalPaOrderFilterKeys.Assignee) {
      const assignee = filtersAfterProcessing[key];
      const assigneeList = assignee.includes("unassigned")
        ? [...assignee, ""]
        : assignee;
      filteredData = filteredData.filter((order) => {
        return assigneeList.includes(order?.assigned_to?.provider_id ?? "");
      });
    }
    if (key === MedicalPaOrderFilterKeys.DateOfService) {
      filteredData = filteredData.filter((order) => {
        // Parse both sides in UTC so the calendar-day comparison matches the
        // UTC-midnight ISO DOS shown elsewhere; the filter bounds are MM/DD/YYYY.
        const serviceDate = moment.utc(order?.payload?.date_of_service);
        const startDate = moment.utc(filtersAfterProcessing[key][0], "MM/DD/YYYY");
        const endDate = moment.utc(filtersAfterProcessing[key][1], "MM/DD/YYYY");

        return (
          serviceDate.isSameOrAfter(startDate, "day") &&
          serviceDate.isSameOrBefore(endDate, "day")
        );
      });
    }

    if (key === MedicalPaOrderFilterKeys.CreatedAt) {
      filteredData = filteredData.filter((order) => {
        // Parse both sides in UTC so the calendar-day comparison matches the
        // UTC created-at shown elsewhere; the filter bounds are MM/DD/YYYY.
        const createdAt = moment.utc(order?.created_at);
        const startDate = moment.utc(filtersAfterProcessing[key][0], "MM/DD/YYYY");
        const endDate = moment.utc(filtersAfterProcessing[key][1], "MM/DD/YYYY");

        return (
          createdAt.isSameOrAfter(startDate, "day") &&
          createdAt.isSameOrBefore(endDate, "day")
        );
      });
    }
    if (key === MedicalPaOrderFilterKeys.EvWriteBackStatus) {
      filteredData = filteredData.filter((order) => {
        const evWriteBackStatus = order?.status?.ev_write_back_status;
        if (evWriteBackStatus) {
          return filtersAfterProcessing[key].includes(evWriteBackStatus);
        }
        return false;
      });
    }
    if (key === MedicalPaOrderFilterKeys.DocumentUploadStatus) {
      filteredData = filteredData.filter((order) => {
        const documentUploadStatus = order?.status?.document_upload_status;
        if (documentUploadStatus) {
          return filtersAfterProcessing[key].includes(documentUploadStatus);
        }
        return false;
      });
    }
  }
  return filteredData;
};
/**
 * Transforms a filter map into the nested array format required by Algolia facet filters.
 * Date-based keys (created_at, date_of_service, date_of_work) are excluded.
 *
 * @param obj - Filter selections keyed by attribute name.
 * @returns Nested string arrays where each inner array is an OR group of "key:value" strings.
 */
export const transformObjectToKeyValueArray = (
  obj: Record<string, string[]>,
): string[][] => {
  return Object.entries(obj)
    .filter(
      ([key]) =>
        key !== "created_at" &&
        key !== "date_of_service" &&
        key !== "date_of_work",
    )
    .map(([key, values]) =>
      values.map((value) => {
        return `${key}:${value}`;
      }),
    );
};

/**
 * Transforms a filter map into the flat key→values object expected by the Postgres search endpoint.
 * Date-based keys are excluded (handled separately by {@link transformDateFields}).
 *
 * @param obj - Filter selections keyed by attribute name.
 * @returns Flat record suitable for the Postgres `facet_filters` parameter.
 */
export const transformObjectToFacetFiltersForPostgres = (
  obj: Record<string, string[]>,
) => {
  return Object.entries(obj)
    .filter(
      ([key]) =>
        key !== "created_at" &&
        key !== "date_of_service" &&
        key !== "date_of_work",
    )
    .reduce((acc, curr) => ({ ...acc, [curr[0]]: curr[1] }), {});
};

/**
 * Extracts date-range filter fields (created_at, date_of_service, date_of_work)
 * and converts them from MM/DD/YYYY to YYYY-MM-DD start/end pairs.
 *
 * @param obj - Filter selections that may contain date arrays.
 * @returns Record with `{field}_start` and `{field}_end` keys in ISO date format.
 */
export const transformDateFields = (obj: Record<string, string[]>) => {
  const result: Record<string, string> = {};

  ["created_at", "date_of_service", "date_of_work"].forEach((field) => {
    if (obj[field]) {
      const formatted = obj[field].map((v) => {
        const [mm, dd, yyyy] = v.split("/");
        return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
      });

      // Sort to find min/max
      const sorted = formatted.sort();
      result[`${field}_start`] = sorted[0];
      result[`${field}_end`] = sorted[sorted.length - 1];
    }
  });

  return result;
};
